import '@styles/editField/TimeFrameCard.css';
import addNewIcon from 'images/icons/edit_field/icon_plus.svg';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  calculateMinMax,
  validateChangedTimeFrame,
  validateByWeekTimeFrame,
} from 'timeFramesManager';
import TimeFrameEdit from './TimeFrameEdit';
import TimeFrameItem from './TimeFrameItem';
const { getSeasonStartDay, getSeasonEndDay } = require('PlannerUtils');
const { useTranslation } = require('react-i18next');
var { unitDisplayName } = require('unitsManager');
const {
  getDayOptions,
  getWeeklyOptions,
  getIntervalOptions,
} = require('timeFramesManager');
const {
  plannerType,
  weeklyPlannerModes,
} = require('@managers/planningManager');
const { shouldWeekStartWithMonday } = require('PlannerUtils');

function useTimeFrames(initialTimeFrames) {
  const [timeFrames, setTimeFrames] = useState([]);
  useEffect(() => {
    if (!initialTimeFrames) return;
    const timeFramesArray = Object.values(initialTimeFrames).map(
      (x, index) => ({
        ...x,
        id: index,
      }),
    );
    setTimeFrames(timeFramesArray);
  }, [initialTimeFrames]);
  return [timeFrames, setTimeFrames];
}

function getTypeConfig(
  field,
  t,
  selectedFarmingUnit,
  selectedDistributionName,
) {
  const plannerSettings = field.settings.planner_settings;
  switch (plannerSettings.type) {
    case plannerType.DAILY_RECOMMENDATION:
      const startWithMonday = shouldWeekStartWithMonday(
        selectedFarmingUnit,
        selectedDistributionName,
      );
      return {
        key: 'day',
        label: t('day'),
        frequencyOptions: getDayOptions(startWithMonday),
        validationFunction: validateChangedTimeFrame,
        timeFramesKey: 'time_frames',
        showHours: true,
      };
    case plannerType.WEEKLY_RECOMMENDATION:
      const baseConfig = {
        key: 'frequency_x',
        label: t('frequency'),
        showHours: false,
        validationFunction: validateByWeekTimeFrame,
        shouldBlockPicker: true,
      };
      if (plannerSettings.mode === weeklyPlannerModes.WEEKLY) {
        return {
          ...baseConfig,
          frequencyOptions: getWeeklyOptions(),
          timeFramesKey: 'weekly_time_frames',
          defaultOption: 7,
        };
      }
      if (plannerSettings.mode === weeklyPlannerModes.INTERVAL) {
        return {
          ...baseConfig,
          frequencyOptions: getIntervalOptions(),
          timeFramesKey: 'interval_time_frames',
          defaultOption: 1,
        };
      }
      throw new Error(
        `Invalid planner mode: ${plannerSettings.mode}. Expected one of: ${Object.values(weeklyPlannerModes).join(', ')}`,
      );
    default:
      throw new Error(
        `Invalid planner type: ${plannerSettings.type}. Expected one of: ${Object.values(plannerType).join(', ')}`,
      );
  }
}

export const TimeFrameWrapper = ({
  field,
  currentPlannerType,
  onIrrigationTimeFramesChange,
  onAdd,
  onEdit,
  userData,
  ...rest
}) => {
  if (currentPlannerType === plannerType.DISABLED) {
    return null;
  }
  const { t } = useTranslation();

  const [typeConfig, setTypeConfig] = useState(null);
  const [timeFrames, setTimeFrames] = useTimeFrames(
    field.irrigation_system[typeConfig?.timeFramesKey],
  );
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  const selectedDistributionName = useSelector(
    (state) => state.distribution_data.selected_entities.distribution?.name,
  );
  const selectedFarmingUnit = useSelector(
    (state) => state.distribution_data.selected_entities.farming_unit,
  );
  useEffect(() => {
    try {
      setTypeConfig(
        getTypeConfig(field, t, selectedFarmingUnit, selectedDistributionName),
      );
    } catch (err) {
      console.log('err', err);
    }
  }, [
    field.id,
    field.settings.planner_settings.type,
    field.settings.planner_settings.mode,
  ]);

  useEffect(() => {
    setTimeFrames(
      Object.values(
        field.irrigation_system[typeConfig?.timeFramesKey] || {},
      ).map((x, index) => ({ ...x, id: index })),
    );
    setEditingId(null);
    setAddingNew(false);
    setOpenMenuId(null);
  }, [field, typeConfig?.timeFramesKey]);

  const soilData = useSelector((state) => state.soil_data.soil_data);
  let userUnits =
    userData && 'length' in userData ? unitDisplayName(userData.length) : '';

  // Get SOW and season end dates
  const sowDate = getSeasonStartDay(field);
  const endOfSeason = getSeasonEndDay(field);
  const userLengthUnits = unitDisplayName(userData.length);
  const firstDayOfWeek = field.settings.planner_settings.first_day_of_week;

  const handleCancel = () => {
    setEditingId(null);
    setAddingNew(false);
  };
  const handleAddNew = () => {
    setAddingNew(true);
    setEditingId(null);
  };

  const handleDelete = (id) => {
    const updated = timeFrames.filter((item) => item.id !== id);
    // TODO: recalculate all effective dates
    // const finalList = recalculateAllEffectiveDates(updated);
    setTimeFrames(updated);
    onIrrigationTimeFramesChange(typeConfig?.timeFramesKey, updated);
  };

  const getTooltipText = (value, units, source, tooltipSource) => {
    if (source.includes('user')) {
      tooltipSource =
        tooltipSource === 'soil'
          ? t('soil_type')
          : t('edit_field_irrigation_timeframe_source_irrigation_rate');
      return (
        t('edit_field_irrigation_timeframe_tooltip') +
        value +
        ' ' +
        units +
        `(${t('source')}: ${tooltipSource})`
      );
    } else {
      return (
        t('edit_field_irrigation_timeframe_tooltip') +
        value +
        ' ' +
        units +
        `(${t('source')}: ${source})`
      );
    }
  };

  // Translate sources as in old code
  const getSourceText = (source) => {
    if (source === 'soil') {
      return t('edit_field_irrigation_timeframe_source_soil');
    } else if (source === 'rate') {
      return t('edit_field_irrigation_timeframe_source_irrigation_rate');
    } else {
      return t('edit_field_irrigation_timeframe_source_user');
    }
  };

  const setSourceContent = (
    irrigationSystem,
    fieldSoilType,
    soilData,
    userUnits,
    timeFrame = {},
    showHours,
  ) => {
    let minTooltipContent = '';
    let maxTooltipContent = '';
    let {
      minPlaceHolder,
      maxPlaceHolder,
      minSource,
      maxSource,
      minTooltipSource,
      maxTooltipSource,
    } = calculateMinMax(
      timeFrame,
      irrigationSystem,
      fieldSoilType,
      soilData,
      userUnits,
      showHours,
    );
    minSource = getSourceText(minSource);
    maxSource = getSourceText(maxSource);
    minTooltipContent = getTooltipText(
      minPlaceHolder,
      userUnits,
      minSource,
      minTooltipSource,
    );
    maxTooltipContent = getTooltipText(
      maxPlaceHolder,
      userUnits,
      maxSource,
      maxTooltipSource,
    );
    return {
      minData: {
        placeholder: minPlaceHolder,
        source: minSource,
        tooltip: minTooltipContent,
      },
      maxData: {
        placeholder: maxPlaceHolder,
        source: maxSource,
        tooltip: maxTooltipContent,
      },
    };
  };

  const NoTimeFrames = () => {
    return (
      <div className='no-time-frames'>
        <div className='no-time-frames-text'>{t('add_time_frames')}</div>
      </div>
    );
  };

  let { minData, maxData } = setSourceContent(
    field.irrigation_system,
    field.soil_type,
    soilData,
    userUnits,
    typeConfig?.showHours,
  );

  const HandleTimeFrameChange = (newTimeFrame, validateFrom) => {
    let errors;
    let finalList = [];
    if (validateFrom === 'edit') {
      // remove old timeframe from timeframes and send to validate edit
      let timeFramesDup = [...timeFrames];
      timeFramesDup.splice(newTimeFrame.id, 1);
      errors = typeConfig?.validationFunction(
        timeFramesDup,
        newTimeFrame,
        sowDate,
        endOfSeason,
      );
      timeFramesDup.push(newTimeFrame);
      finalList = timeFramesDup;
    } else {
      errors = typeConfig?.validationFunction(
        timeFrames,
        newTimeFrame,
        sowDate,
        endOfSeason,
      );
      finalList = [...timeFrames, newTimeFrame];
    }
    if (errors.length > 0) {
      return errors;
    } else {
      setTimeFrames(finalList);
      setAddingNew(false);
      setEditingId(null);
      onIrrigationTimeFramesChange(typeConfig?.timeFramesKey, finalList);
    }
  };

  return (
    <div>
      <div className='d-flex flex-column w-100 aling-item-center justify-content-around ml-2 mr-2 pr-4 pt-2 mb-3'>
        <div className='d-flex flex-row justify-content-between'>
          <label className='text-grey45 text-size-16 mb-0 d-block font-weight-bold'>
            {t('irrigation_time_frames')}
          </label>
          <button
            type='button'
            className='btn btn-primary pr-2 pl-2 blue-primary align-items-center d-flex justify-content-center'
            onClick={handleAddNew}
            style={{
              fontSize: 12,
              background: addingNew ? '#D0D3D9' : '#4563C7',
              height: '24px',
              padding: '3px 0px 3px',
            }}
            disabled={addingNew}
          >
            <img
              src={addNewIcon}
              style={{ height: '12px', width: '12px' }}
              className='mx-1'
              alt='add new timeframe'
            />
            {t('add_new_button')}
          </button>
        </div>
        <small
          className='text-grey71 pt-2 pb-0'
          style={{ lineHeight: 1, whiteSpace: 'pre-line' }}
        >
          {`${t('define_time_frames')}\n${t('edit_field_timeframe_no_frequnecy_constraint')}`}
        </small>
      </div>
      {addingNew && (
        <TimeFrameEdit
          key={timeFrames?.length}
          typeConfig={typeConfig}
          onSave={(data) => HandleTimeFrameChange(data, 'add')}
          id={timeFrames?.length}
          onCancel={handleCancel}
          userLengthUnits={userLengthUnits}
          minData={minData}
          maxData={maxData}
          sowDate={sowDate}
          endOfSeason={endOfSeason}
          firstDayOfWeek={firstDayOfWeek}
          {...rest}
        />
      )}
      {timeFrames.length === 0 && !addingNew && <NoTimeFrames />}
      {timeFrames.map((tf) => {
        let { minData, maxData } = setSourceContent(
          field.irrigation_system,
          field.soil_type,
          soilData,
          userUnits,
          tf,
          typeConfig?.showHours,
        );
        tf.active_period_start_date = tf.active_period_start_date || sowDate;
        if (editingId === tf.id) {
          return (
            <TimeFrameEdit
              key={tf.id}
              tf={tf}
              typeConfig={typeConfig}
              onSave={(data) => HandleTimeFrameChange(data, 'edit')}
              onCancel={handleCancel}
              userLengthUnits={userLengthUnits}
              minData={minData}
              maxData={maxData}
              sowDate={sowDate}
              endOfSeason={endOfSeason}
              firstDayOfWeek={firstDayOfWeek}
              {...rest}
            />
          );
        } else {
          return (
            <TimeFrameItem
              key={tf.id}
              tf={tf}
              typeConfig={typeConfig}
              onEdit={() => {
                setEditingId(tf.id);
                setOpenMenuId(null);
                setAddingNew(false);
              }}
              onDelete={() => handleDelete(tf.id)}
              userLengthUnits={userLengthUnits}
              showMenu={openMenuId === tf.id}
              onMenuToggle={(id) =>
                setOpenMenuId(openMenuId === id ? null : id)
              }
              minData={minData}
              maxData={maxData}
              {...rest}
            />
          );
        }
      })}
    </div>
  );
};
